import React from "react";
var __jsx = React.createElement;
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { showSuccessToast } from "@/components/toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/pro-regular-svg-icons";
import { UnstyledButton } from "@mantine/core";
import { useTaskLabels, useCreateReminder, useUpdateActionState } from "@/hooks/tasks";
import { useWorkspaceUsers } from "@/hooks/use-workspaces";
import { useEmrContext } from "@/hooks/emr-context";
import { Button } from "@/components/button";
import { useTaskActions } from "@/components/task-actions-provider";
import { trackEvent, ANALYTICS_EVENTS } from "@/utils/analytics";
import { formatDate } from "@/utils/format";
import { toDateInputValue, fromDateInputValue } from "@/utils/date";
import { LinkPatientRecordsModal } from "@/components/link-patient-records-modal";
import { LinkedRecordsPanel } from "@/components/linked-records-panel";
import { TaskActionSummary } from "./task-action-summary";
import { TaskActionFields } from "./task-action-fields";
import { TaskActionLinkRecords } from "./task-action-link-records";
var EMPTY_LINKED_RESOURCES = {
  reports: [],
  appointments: [],
  encounters: []
};

/** A single task action row — pre-filled form to create a task; once created it shows a done marker. */
export function TaskAction(_ref) {
  var action = _ref.action,
    patientUid = _ref.patientUid,
    reminderId = _ref.reminderId,
    isDone = _ref.isDone,
    onDone = _ref.onDone,
    onDelete = _ref.onDelete;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var _useEmrContext = useEmrContext(),
    workspaceId = _useEmrContext.workspaceId;
  var _useTaskActions = useTaskActions(),
    onTaskCreated = _useTaskActions.onTaskCreated;
  var queryClient = useQueryClient();
  var payload = action.payload;
  var _useState = useState(payload.title),
    title = _useState[0],
    setTitle = _useState[1];
  var _useState2 = useState(payload.label ? String(payload.label.id) : null),
    labelId = _useState2[0],
    setLabelId = _useState2[1];
  var _useState3 = useState(payload.owned_by_info ? String(payload.owned_by_info.id) : null),
    ownedBy = _useState3[0],
    setOwnedBy = _useState3[1];
  var _useState4 = useState(toDateInputValue(payload.remind_after)),
    dueDate = _useState4[0],
    setDueDate = _useState4[1];
  var _useState5 = useState(EMPTY_LINKED_RESOURCES),
    linkedResources = _useState5[0],
    setLinkedResources = _useState5[1];
  var _useState6 = useState(false),
    isLinkOpen = _useState6[0],
    setIsLinkOpen = _useState6[1];
  var _useTaskLabels = useTaskLabels(workspaceId),
    _useTaskLabels$data = _useTaskLabels.data,
    taskLabels = _useTaskLabels$data === void 0 ? [] : _useTaskLabels$data;
  var _useWorkspaceUsers = useWorkspaceUsers(workspaceId),
    _useWorkspaceUsers$da = _useWorkspaceUsers.data,
    workspaceUsers = _useWorkspaceUsers$da === void 0 ? [] : _useWorkspaceUsers$da;
  var _useCreateReminder = useCreateReminder(patientUid ? String(patientUid) : null),
    createTask = _useCreateReminder.mutate,
    isCreating = _useCreateReminder.isLoading;
  var _useUpdateActionState = useUpdateActionState(patientUid, reminderId),
    updateState = _useUpdateActionState.mutate,
    isUpdatingState = _useUpdateActionState.isLoading;
  var labelOptions = taskLabels.map(function (l) {
    return {
      value: String(l.id),
      label: l.name
    };
  });
  var ownerOptions = workspaceUsers.map(function (u) {
    return {
      value: String(u.id),
      label: u.name
    };
  });
  var buildPayload = function buildPayload() {
    var _payload$description;
    return {
      title: title,
      context: (_payload$description = payload.description) !== null && _payload$description !== void 0 ? _payload$description : "",
      remind_after: fromDateInputValue(dueDate),
      owned_by: ownedBy ? Number(ownedBy) : null,
      label_ids: labelId ? [Number(labelId)] : [],
      linked_resources: linkedResources
    };
  };
  var isCreateDisabled = !title.trim() || !ownedBy || !dueDate;
  var isLinkedResourcesEmpty = linkedResources.reports.length === 0 && linkedResources.appointments.length === 0 && linkedResources.encounters.length === 0;
  var openLinkModal = function openLinkModal() {
    return setIsLinkOpen(true);
  };
  var handleSelectPatientRecord = function handleSelectPatientRecord() {
    trackEvent(ANALYTICS_EVENTS.TASK_ACTION_SELECT_PATIENT_RECORD, {
      actionId: action.id
    });
    openLinkModal();
  };
  var handleCreate = function handleCreate() {
    if (isCreateDisabled) return;
    trackEvent(ANALYTICS_EVENTS.TASK_ACTION_CREATE, {
      actionId: action.id
    });
    createTask(buildPayload(), {
      onSuccess: function onSuccess() {
        queryClient.invalidateQueries(["patient-reminders"]);
        var message = t("toast.task_created", {
          defaultValue: "Task created successfully"
        });
        if (onTaskCreated) onTaskCreated(message);else showSuccessToast(message);
        updateState({
          actionId: action.id,
          state: "done"
        }, {
          onSuccess: onDone
        });
      }
    });
  };
  if (isDone) {
    var _labelOptions$find, _ownerOptions$find;
    var subtitle = [(_labelOptions$find = labelOptions.find(function (o) {
      return o.value === labelId;
    })) === null || _labelOptions$find === void 0 ? void 0 : _labelOptions$find.label, (_ownerOptions$find = ownerOptions.find(function (o) {
      return o.value === ownedBy;
    })) === null || _ownerOptions$find === void 0 ? void 0 : _ownerOptions$find.label, dueDate ? t("label.due_on", {
      defaultValue: "Due {{date}}",
      date: formatDate(dueDate)
    }) : ""].filter(Boolean).join(" · ");
    return /*#__PURE__*/React.createElement("div", {
      className: "rounded-sds-200 border border-transparent bg-sds-base-secondary"
    }, /*#__PURE__*/React.createElement(TaskActionSummary, {
      title: title,
      subtitle: subtitle
    }));
  }
  return /*#__PURE__*/React.createElement("div", {
    className: "rounded-sds-200 border border-sds-base-default bg-sds-base-default flex flex-col"
  }, /*#__PURE__*/React.createElement(TaskActionFields, {
    actionId: action.id,
    title: title,
    labelId: labelId,
    ownedBy: ownedBy,
    dueDate: dueDate,
    labelOptions: labelOptions,
    ownerOptions: ownerOptions,
    onTitleChange: setTitle,
    onLabelChange: setLabelId,
    onOwnerChange: setOwnedBy,
    onDueDateChange: setDueDate
  }), !isLinkedResourcesEmpty && /*#__PURE__*/React.createElement("div", {
    className: "px-3 pt-1"
  }, /*#__PURE__*/React.createElement(LinkedRecordsPanel, {
    linkedResources: linkedResources,
    onAddRecords: openLinkModal,
    onViewRecords: openLinkModal
  })), /*#__PURE__*/React.createElement("div", {
    className: "px-3 pb-3 pt-2 flex items-center justify-between gap-2"
  }, isLinkedResourcesEmpty ? /*#__PURE__*/React.createElement(TaskActionLinkRecords, {
    actionId: action.id,
    onOpenLinkRecords: handleSelectPatientRecord
  }) : /*#__PURE__*/React.createElement("span", {
    className: "flex-1"
  }), /*#__PURE__*/React.createElement("div", {
    className: "flex items-center gap-2 flex-shrink-0"
  }, /*#__PURE__*/React.createElement(Button, {
    "data-testid": "task-action-create-".concat(action.id),
    intent: "brand",
    variant: "secondary",
    size: "sm",
    loading: isCreating || isUpdatingState,
    disabled: isCreateDisabled,
    onClick: handleCreate
  }, t("button.create", {
    defaultValue: "Create"
  })), /*#__PURE__*/React.createElement(UnstyledButton, {
    "data-testid": "task-action-delete-".concat(action.id),
    "aria-label": t("button.delete", {
      defaultValue: "Delete"
    }),
    onClick: function onClick() {
      trackEvent(ANALYTICS_EVENTS.TASK_ACTION_DELETE, {
        actionId: action.id
      });
      onDelete(action.id);
    },
    className: "w-7 h-7 flex items-center justify-center rounded-sds-100 text-sds-base-secondary hover:bg-sds-urgent-tertiary hover:text-sds-urgent-default transition-colors flex-shrink-0"
  }, /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: faTrash,
    className: "w-3.5 h-3.5"
  })))), /*#__PURE__*/React.createElement(LinkPatientRecordsModal, {
    show: isLinkOpen,
    onClose: function onClose() {
      return setIsLinkOpen(false);
    },
    onLink: function onLink(resources) {
      setLinkedResources(resources);
      setIsLinkOpen(false);
    },
    linkedRes: linkedResources
  }));
}